import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { NAV_LINKS, SITE_URL } from "@/data/services";
import { useSiteContent } from "@/hooks/useSiteContent";

const LOGO_URL =
  "https://wewonderevents.com/wp-content/uploads/2026/07/cropped-WE-logo-png-scaled-1-300x218.png";

const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();
  const isActive = (link) => link.href === pathname;
  const s = useSiteContent();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header data-testid="site-header" className="sticky top-0 z-50">
      <div className="relative h-[140px] lg:h-[156px]">
        <div
          aria-hidden="true"
          className={`absolute inset-x-0 bottom-0 top-[70px] hidden bg-white/95 backdrop-blur transition-all duration-300 lg:top-[78px] lg:block ${
            scrolled
              ? "opacity-100 shadow-[0_6px_24px_rgba(0,0,0,0.08)]"
              : "pointer-events-none opacity-0"
          }`}
        />

        <a
          href={SITE_URL}
          data-testid="header-logo"
          aria-label="Wonder Events Home"
          className="absolute left-[10px] top-[4px] z-10 block lg:left-[-15px] lg:top-[-65px]"
        >
          <span className="flex h-[52px] w-[52px] items-center justify-center rounded-full border-[3px] border-[#C62828] bg-white lg:h-[220px] lg:w-[220px] lg:items-end lg:border-4 lg:pb-[28px]">
            <img
              src={LOGO_URL}
              alt="Wonder Events & Entertainment Pvt. Ltd."
              className="h-auto w-[36px] lg:w-[145px]"
            />
          </span>
        </a>

        <div className={`relative flex h-[60px] items-center justify-end bg-white/95 backdrop-blur transition-shadow duration-300 lg:h-[78px] ${
          scrolled ? "shadow-[0_4px_24px_rgba(0,0,0,0.08)] lg:shadow-none" : ""
        }`}>
          <nav data-testid="desktop-nav" className="hidden items-center gap-10 pr-12 lg:flex">
            {NAV_LINKS.map((link) => (
              <a
                key={link.label}
                data-testid={`nav-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                href={link.href}
                className={`text-base font-medium transition-colors duration-200 ${
                  isActive(link) ? "text-[#C62828]" : "text-[#333333] hover:text-[#C62828]"
                }`}
              >
                {link.label}
              </a>
            ))}
            <a
              data-testid="header-contact-cta"
              href={s.contact_url}
              className="inline-flex items-center gap-2 rounded-full bg-[#C62828] px-5 py-2.5 text-[13px] font-bold uppercase tracking-wider text-white shadow-[0_8px_20px_rgba(198,40,40,0.35)] transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#a51f1f]"
            >
              <Phone size={14} strokeWidth={2.5} />
              {s.header_button_text}
            </a>
          </nav>

          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            className="mr-5 flex h-11 w-11 items-center justify-center text-[#1F1F1F] lg:hidden"
            aria-label="Toggle navigation"
          >
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
      </div>

      {open && (
        <nav
          data-testid="mobile-nav"
          className="border-t border-[#F0F0F0] bg-white px-5 pb-5 pt-1 shadow-lg lg:hidden"
        >
          {NAV_LINKS.map((link) => (
            <a
              key={link.label}
              data-testid={`mobile-nav-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
              href={link.href}
              onClick={() => setOpen(false)}
              className={`block border-b border-[#F5F5F5] py-3.5 text-base font-medium ${
                isActive(link) ? "text-[#C62828]" : "text-[#333333]"
              }`}
            >
              {link.label}
            </a>
          ))}
          <a
            data-testid="mobile-header-contact-cta"
            href={s.contact_url}
            className="mt-4 flex items-center justify-center gap-2 rounded-full bg-[#C62828] px-5 py-3.5 text-sm font-bold uppercase tracking-wider text-white"
          >
            <Phone size={15} strokeWidth={2.5} />
            {s.header_button_text}
          </a>
        </nav>
      )}
    </header>
  );
};

export default Header;
